import type { IntegrationProvider } from '@lizrd/contracts';
import { PROVIDER_ACCENT } from '../lib/graph-visuals';
import { Modal } from './ui/modal';
import { ProviderLogo } from './ProviderLogo';

type Option = {
  provider: IntegrationProvider;
  label: string;
  blurb: string;
  soon?: boolean;
};

// Clouds first (what you run + what it costs), then code hosts (how it's used). A
// source can be connected more than once (e.g. several AWS accounts). Sandbox is
// intentionally absent — it's demo data, never a real customer source.
const CLOUDS: Option[] = [
  { provider: 'AWS', label: 'AWS', blurb: 'Cloud resources & cost' },
  { provider: 'GCP', label: 'GCP', blurb: 'Cloud resources & cost' },
  { provider: 'AZURE', label: 'Azure', blurb: 'Cloud resources & cost' },
];
const CODE: Option[] = [
  { provider: 'GITHUB', label: 'GitHub', blurb: 'Repositories, code & IaC' },
  { provider: 'GITLAB', label: 'GitLab', blurb: 'Repositories, code & IaC' },
];

/** Provider picker opened from the graph's generic "Connect source" node. When
 * `kind` is set, only that family is offered (e.g. the onboarding cloud box opens
 * a clouds-only picker). */
export function ConnectSourceModal({
  onClose,
  onPick,
  kind,
}: {
  onClose: () => void;
  onPick: (provider: IntegrationProvider) => void;
  kind?: 'cloud' | 'code';
}) {
  const groups =
    kind === 'cloud'
      ? ([{ heading: 'Clouds', items: CLOUDS }] as const)
      : kind === 'code'
        ? ([{ heading: 'Code repositories', items: CODE }] as const)
        : ([
            { heading: 'Clouds', items: CLOUDS },
            { heading: 'Code repositories', items: CODE },
          ] as const);
  const title =
    kind === 'cloud'
      ? 'Connect a cloud'
      : kind === 'code'
        ? 'Connect a code base'
        : 'Connect a source';
  return (
    <Modal
      open
      onClose={onClose}
      title={<h3 className="font-display text-base font-bold text-bone">{title}</h3>}
    >
      <p className="mb-4 text-sm text-muted">
        Pick a system to stream into this domain. The more you connect, the sharper Lizrd’s guidance
        — and you can connect several accounts of the same provider.
      </p>
      {groups.map((group) => (
        <div key={group.heading} className="mb-4 last:mb-0">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            {group.heading}
          </p>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {group.items.map((opt) => {
              const accent = PROVIDER_ACCENT[opt.provider] ?? '#8b93a7';
              return (
                <button
                  key={opt.provider}
                  onClick={() => !opt.soon && onPick(opt.provider)}
                  disabled={opt.soon}
                  className="flex flex-col items-center gap-1 rounded-lg border border-hairline bg-surface-2/40 p-3 text-center transition-colors enabled:hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                  style={
                    opt.soon
                      ? undefined
                      : { borderColor: `color-mix(in srgb, ${accent} 45%, transparent)` }
                  }
                >
                  <ProviderLogo
                    provider={opt.provider}
                    size={20}
                    className={opt.soon ? 'grayscale' : undefined}
                  />
                  <span className="text-sm font-semibold text-bone">{opt.label}</span>
                  <span className="text-[0.65rem] text-muted">{opt.blurb}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Modal>
  );
}
